import axios, { CancelToken } from 'axios';
import { CANCEL } from 'redux-saga';

axios.defaults.headers.post['Content-Type'] = 'application/json; charset=utf-8';

/**
 * Build authorization header from Bearer Token
 * @param  {String} token
 * @param  {Object} config
 * @return {(Object|null)} the auth header
 */
const buildHeaders = (token, config) => {
    let headers = null;
    if (token) {
        headers = { Authorization: `Bearer ${token}` };
    }
    if (config && config.headers) {
        headers = {
            ...headers,
            ...config.headers,
        };
        // eslint-disable-next-line no-param-reassign
        delete config.headers;
    }
    return headers;
};

/**
 * Send a generic request to an endpoint
 * @param  {String} method - axios method (get, put, post, ...)
 * @param  {String} url
 * @param  {Object} config - any config to set/override, including params  & data
 *                           Full list of config parameters can be found at https://github.com/axios/axios#request-config
 * @param  {String} token  - the bearer token; auth header will be build based on this
 * @return {Promise.<Object>} axios response as described at https://github.com/axios/axios#response-schema
 */
const request = (method, url, config, token) => {
    const headers = buildHeaders(token, config);
    const source = CancelToken.source();
    const requestPromise = axios.request({
        method,
        url,
        headers,
        cancelToken: source.token,
        ...config,
    });
    // .catch((error) => {
    //     if (error.response) {
    //         // The request was made and the server responded with a status code
    //         // that falls out of the range of 2xx
    //         throw Boom.boomify(error, {
    //             statusCode: error.response.status,
    //             message: error.response.statusText,
    //         });
    //     }
    //     // The request was made but no response was received or
    //     // something happened in setting up the request that triggered an Error
    //     throw error;
    // });
    // Attach function to be called by redux-saga when a saga is cancelled
    requestPromise[CANCEL] = () => source.cancel();
    return requestPromise;
};

/**
 * Send a PUT request
 * @param  {String} url
 * @param  {Object} data - to be sent as the request body
 * @param  {String} token
 * @param  {Object} config - extra axios config
 * @return {Promise.<Object>} axios response
 */
const put = (url, data, token, config = {}) => request('put', url, { ...config, data }, token);

/**
 * Send a POST request
 * @param  {String} url
 * @param  {Object} data - to be sent as the request body
 * @param  {String} token
 * @param  {Object} config - extra axios config
 * @return {Promise.<Object>} axios response
 */
const post = (url, data, token, config = {}) => request('post', url, { ...config, data }, token);

/**
 * Send a GET request
 * @param  {String} url
 * @param  {Object} params - to be sent as URL parameters with the request
 * @param  {String} token
 * @param  {Object} config - extra axios config
 * @return {Promise.<Object>} axios response
 */
const get = (url, params, token, config = {}) => request('get', url, { ...config, params }, token);

/**
 * Send a PATCH request
 * @param  {String} url
 * @param  {Object} data - to be sent as the request body
 * @param  {String} token
 * @param  {Object} config - extra axios config
 * @return {Promise.<Object>} axios response
 */
const patch = (url, data, token, config = {}) => request('patch', url, { ...config, data }, token);

/**
 * Send a DELETE request - name conflict with `delete` keyword therefore named deletex
 * @param  {String} url
 * @param  {?Object} params - optional params to be sent as URL parameters with the request
 * @param  {String} token
 * @param  {Object} config - extra axios config
 * @return {Promise.<Object>} axios response
 */
const deletex = (url, params, token, config = {}) => request('delete', url, { ...config, params }, token);

/**
 * Send a DELETE request - but sends extras params in the body
 * @param  {String} url
 * @param  {?Object} data - to be sent as the request body
 * @param  {String} token
 * @param  {Object} config - extra axios config
 * @return {Promise.<Object>} axios response
 */
const deleteWithBody = (url, data, token, config = {}) => request('delete', url, { ...config, data }, token);

export { request, post, get, put, patch, deletex, deleteWithBody };
